import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import { Field } from "@/ui";

/**
 * Client-side validation against the SAME Zod schemas the API validates
 * with (see packages/shared/src/schemas). Structural types here — rather
 * than importing `zod` directly — so any schema's `safeParse()` result
 * works without adding a direct zod dependency to this app.
 */
interface ZodIssueLike {
  path: PropertyKey[];
  message: string;
}

interface ZodErrorLike {
  issues: ZodIssueLike[];
}

interface SafeParseResult<T> {
  success: boolean;
  data?: T;
  error?: ZodErrorLike;
}

interface SchemaLike<T> {
  safeParse(value: unknown): SafeParseResult<T>;
}

/** Flattens Zod issues into `{ "a.b": "message" }`, keyed the same way `TextInput error=` looks them up. */
export function zodFieldErrors(error: ZodErrorLike): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const issue of error.issues) {
    const path = issue.path.join(".");
    if (!fields[path]) fields[path] = issue.message;
  }
  return fields;
}

/** Full-form validation for submit. On success, `data` is already normalised (e.g. phone becomes +91XXXXXXXXXX) — send that, not the raw form state. */
export function validateForm<T>(
  schema: SchemaLike<T>,
  payload: unknown,
): { success: true; data: T } | { success: false; fields: Record<string, string> } {
  const result = schema.safeParse(payload);
  if (result.success && result.data !== undefined) {
    return { success: true, data: result.data };
  }
  return { success: false, fields: zodFieldErrors(result.error ?? { issues: [] }) };
}

/** Revalidates the whole payload but only applies the result to one field — for onBlur. */
export function validateFieldOnBlur<T>(
  schema: SchemaLike<T>,
  payload: unknown,
  field: string,
  setFields: Dispatch<SetStateAction<Record<string, string>>>,
): void {
  const result = schema.safeParse(payload);
  setFields((prev) => {
    if (!(field in prev) && result.success) return prev;
    const next = { ...prev };
    if (result.success) {
      delete next[field];
      return next;
    }
    const issue = result.error?.issues.find((i) => i.path.join(".") === field);
    if (issue) {
      next[field] = issue.message;
    } else {
      delete next[field];
    }
    return next;
  });
}

/** Clears one field's error as soon as the user edits it again. */
export function clearFieldError(
  setFields: Dispatch<SetStateAction<Record<string, string>>>,
  field: string,
): void {
  setFields((prev) => {
    if (!(field in prev)) return prev;
    const next = { ...prev };
    delete next[field];
    return next;
  });
}

export interface PhoneFieldProps {
  label: string;
  value: string;
  onChange: (digits: string) => void;
  onBlur?: () => void;
  error?: string;
  hint?: string;
  required?: boolean;
}

const CONTROL =
  "w-full rounded-md border bg-raised px-3 text-ink placeholder:text-muted/70 " +
  "transition-colors focus:border-brand disabled:cursor-not-allowed disabled:opacity-60";

/** 10-digit mobile number with a fixed +91 prefix — see apps/web/src/pages/auth/KarigarLogin.tsx for the pattern this copies. */
export function PhoneField({ label, value, onChange, onBlur, error, hint, required }: PhoneFieldProps) {
  return (
    <Field error={error} hint={hint} label={label} required={required}>
      {(id, invalid) => (
        <div className="flex gap-2">
          <span className="flex h-10 shrink-0 items-center rounded-md border border-rule bg-raised px-3 text-sm text-muted">
            +91
          </span>
          <input
            aria-describedby={invalid ? `${id}-error` : undefined}
            aria-invalid={invalid}
            autoComplete="tel-national"
            className={`${CONTROL} h-10 flex-1 ${invalid ? "border-danger" : "border-rule"}`}
            id={id}
            inputMode="numeric"
            maxLength={10}
            onBlur={onBlur}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange(e.target.value.replace(/\D/g, "").slice(0, 10))
            }
            type="tel"
            value={value}
          />
        </div>
      )}
    </Field>
  );
}
